import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as _ from 'lodash-es';
//import { DowngradeInjectable } from '@angular/upgrade/static';
//declare var angular: angular.IAngularStatic;
import { NotificationService } from './core.notification.service';
import { StateService, UIRouter } from '@uirouter/angular';

/**
 * State names shared by the login / refusal machinery. They live here because
 * StateHelperService is the lowest layer that needs them — AccessDeniedService imports
 * them from here, and importing the other way round would be a cycle.
 */
export const LOGIN_STATE = 'home.user.login';
export const SITE_HOME_STATE = 'home';
export const PROJECT_HOME_STATE = 'home.project.summary';

@Injectable()
export class StateHelperService {
    private http;

    constructor(http: HttpClient, private notificationservice: NotificationService, private window: Window, private stateService: StateService, private router: UIRouter) {
        this.http = http;
    }

    /**
     * Follow a `url` param produced by buildReturnUrl.
     *
     * The value travels through the login state as a query parameter, so it arrives
     * percent-encoded. Returns false when there is nothing to follow, so callers can fall
     * back to their own default destination.
     */
    navigateByReturnUrl(rawUrl: string): boolean {
        if (!rawUrl) return false;

        let target = rawUrl;
        try {
            target = decodeURIComponent(rawUrl);
        } catch (e) {
            // Malformed percent-encoding — fall back to the raw value.
        }

        // Never follow a return URL back to the login screen: that is how a redirect
        // loop starts, and there is nothing useful at the other end of it.
        if (this.matchStateName(target) === LOGIN_STATE) return false;

        this.navigateByUrl(target);
        return true;
    }

    /** The ui-router state a URL resolves to, or null when it matches nothing. */
    private matchStateName(url: string): string {
        try {
            const parsedUrl = url.startsWith('http')
                ? new URL(url)
                : new URL(url, this.window.location.origin);
            const searchParams: { [key: string]: any } = {};
            parsedUrl.searchParams.forEach((value, key) => {
                searchParams[key] = value;
            });
            const matched = this.router.urlService.match({
                path: parsedUrl.pathname,
                search: searchParams
            });
            return _.get(matched, 'rule.state.name', null);
        } catch (e) {
            return null;
        }
    }

    /**
     * Build the URL to come back to once the user has logged in (the `url` param of
     * `home.user.login`), which `navigateByUrl` above consumes on the way back.
     *
     * Two conventions for this used to coexist: `stateService.href()` of the pending
     * transition in the route resolves, and `window.location` in the interceptor and
     * `UserService.verify()`. They disagree mid-transition, where `window.location`
     * still holds the *previous* URL rather than the one that was refused, so prefer
     * the transition target whenever we have one.
     */
    buildReturnUrl(transition?: any): string {
        if (transition && typeof transition.to === 'function') {
            try {
                const href = this.stateService.href(transition.to().name, transition.params());
                if (href) return href;
            } catch (e) {
                // Fall through to the location-based URL below.
            }
        }
        // pathname + search + hash, in that order. The old interceptor built this as
        // pathname + hash + search, which yields "/a#frag?q=1" — navigateByUrl parses that
        // back with `new URL()`, so the query ended up inside the fragment and was lost on
        // the way back.
        return this.window.location.pathname + this.window.location.search + this.window.location.hash;
    }

    /**
     * Navigate to a URL path using SPA navigation when possible.
     * Matches the URL against known UI-Router states and navigates via stateService.go().
     * Falls back to a full page navigation if no matching state is found.
     * Returns true if SPA navigation was used, false if it fell back to full page load.
     */
    navigateByUrl(url: string): boolean {
        try {
            let parsedUrl: URL;
            if (url.startsWith('http')) {
                parsedUrl = new URL(url);
            } else {
                parsedUrl = new URL(url, this.window.location.origin);
            }
            const path = parsedUrl.pathname;
            const searchParams: { [key: string]: any } = {};
            parsedUrl.searchParams.forEach((value, key) => {
                searchParams[key] = value;
            });
            const hash = parsedUrl.hash;
            const matched = this.router.urlService.match({ path, search: searchParams });
            if (matched && matched.rule && matched.rule['state']) {
                // Same-page click: skip the transition (would re-run resolvers and reset scroll) and just apply the hash.
                const targetStateName = matched.rule['state'].name;
                const samePage = this.stateService.current && this.stateService.current.name === targetStateName
                    && _.isEqual(matched.match || {}, _.pick(this.stateService.params || {}, _.keys(matched.match || {})));
                if (samePage) {
                    this.applyHash(hash);
                    return true;
                }

                this.stateService.go(targetStateName, matched.match, { inherit: false })
                    .then(() => this.applyHash(hash), () => undefined);
                return true;
            }
        } catch (e) {
            // URL didn't match any known state
        }
        this.window.location.href = url;
        return false;
    }

    /** Update location.hash and scroll to the matching element (or top, on bare `#`), deferred so DOM mutations settle first. */
    applyHash(hash: string): void {
        if (!hash) return;
        const raw = hash.startsWith('#') ? hash.slice(1) : hash;
        let id = '';
        try {
            id = decodeURIComponent(raw);
        } catch (e) {
            id = raw; // Malformed percent-encoding — fall back to the literal string.
        }
        if (this.window.location.hash !== hash) {
            this.window.history.replaceState(null, '', this.window.location.pathname + this.window.location.search + hash);
        }
        setTimeout(() => {
            if (!id) {
                this.window.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }
            const el = this.window.document.getElementById(id);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 0);
    }

    getStateForSectionRefId(section: string, refId: number = 0, project: string = "") {
        switch (section) {
            case 'tracker':
                return ['home.project.summary.admin.tracker', { unixName: project, id: refId }];
            case 'trackeritem':
                return ['home.trackeritem', { id: refId }];
            case 'trackersprint':
                return ['home.project.summary.sprint', { unixName: project, id: refId }];
            case 'project':
                return ['home.project.summary', { unixName: (project?.length > 0 ? project : refId) }];
            case 'project-team-joins':
                return ['home.project.summary.team', { unixName: project, view: 'joins' }];
            case 'build':
                return ['home.project.summary.builds.detail', { id: refId, unixName: project }];
            case 'frspackage':
                return ['home.project.summary.frs', { packageId: refId, unixName: project }];
            case 'frsrelease':
                return ['home.project.summary.frs.details', { releaseId: refId, unixName: project }];
            case 'commit':
            case 'scmcommit':
                return ['home.project.summary.scm.commit', { commithash: refId, unixName: project }];
            case 'scm':
                return ['home.project.summary.scm.commits', { unixName: project }];
            case 'site-admin-projects':
                return ['home.admin.projects', {}];
            case 'site-admin-users':
                return ['home.admin.users', {}];
            case 'site-admin-user-lock':
                return ['home.admin.users.edit', { unixName: refId}];
            case 'healthcheckresult':
                return ['home.project.summary.admin.checks', { unixName: project, id: refId }];
            default:
                return false;
        }
    }

    // This function seems so very wrong, but it's required for outbound links from SAC.
    // The SAC JS bundle does not include ui-router configuration for the main app,
    // so $state.href() returns null for any app-related routes.
    // NOTE:  Some sections in here do not relate strictly to entities, but
    //        to specialized locations for those entites.
    getUrlForSectionRefId(section: string, refId: number = 0, project: string = "") {
        switch (section) {
            case 'tracker':
                return '/project/' + project + '/trackeradmin/' + refId;
            case 'trackeritems':
                return '/project/' + project + '/tracker/' + refId + '/browse';
            case 'trackeritem':
                return '/trackeritem/' + refId;
            case 'trackersprint':
                return '/project/' + project + '/sprint/' + refId;
            case 'project':
                return '/project/' + refId;
            case 'build':
                return '/project/' + project + '/builds/' + refId;
            case 'frspackage':
                return '/project/' + project + '/frs?packageId=' + refId;
            case 'frsrelease':
                return '/project/' + project + '/frs/' + refId + '/details';
            case 'commit':
            case 'scmcommit':
                return '/project/' + project + '/scm/commit/' + refId;
            case 'site-admin-projects':
                return '/admin/projects';
            case 'site-admin-users':
                return '/admin/users';
            case 'site-admin-user-lock':
                return '/admin/users/'+ refId + '/edit';
            case 'healthcheckresult':
                return '/project/' + project + '/admin/checks?id=' + refId;
            default:
                return false;
        }
    }

    serialize(obj) {
        var str = [];
        for (var p in obj)
            if (obj.hasOwnProperty(p)) {
                str.push(p + "=" + obj[p]);
            }
        return str.join("&");
    }

    /**
     * Goes to the item specified by section/refId
     * It will go to the state if available, if not will go to a new page
     *
     * @param string section Section of the item
     * @param int refId Id of the item
     * @param object Some options to control browser behavior:
     *              popup: (false) will make browser launch a popup
     *              newWindow: (true) will open a new window (will open in a parent window is available)
     *              target: (_blank) Specifies the target for window.open()
     */
    goToItem(section: string, refId: number = 0, options: any, project: string = "") {
        options = options || {};
        var target = options.target || '_blank';
        var openState = (state, url) => {
            try {
                if (options.newWindow || options.popup) {
                    throw 1; //skip states, go to a new page
                }
                return this.stateService.go(state[0], state[1]);
            } catch (e) {
                if (options.popup) {
                    var newWindow = this.window.open(url, target, options);
                    newWindow.focus();
                    return newWindow;
                } else if (options.newWindow) {
                    var newWindow = this.window.open(url);
                    newWindow.focus();
                    return newWindow;
                } else {
                    // Try SPA navigation with the URL
                    if (state && state[0]) {
                        return this.stateService.go(state[0], state[1], { inherit: false });
                    }
                    // Fall back to a full page load when no state is available
                    if (url) {
                        this.window.location.href = url;
                    }
                    return true;
                }
            }
        }

        //For now forum section is a reference for project chatrooms
        //If we get forum section here it means chat is closed (wall messages for chat mentions are fired only if user is offline)
        //There is no state for chat so we need to know its project, go there and activate chat
        if (section == 'forum') {
            this.http.get(`/api/forum/${refId}`, { params: { rel: 'isChat' } })
            .toPromise()
            .then((response) => {
                if (response.rel.isChat) {
                    var state = this.getStateForSectionRefId(response.section, response.refId);
                    
                    this.stateService.go(state[0], state[1]).then(() => {
                        this.notificationservice.notifyAll('EMBEDDED_CHAT_STATE', { state: 'visible' });
                    });
                } else {
                    var st = ['home.project.summary.forum.read', {
                        unixName: response.refId,
                        id: response.id
                    }];
                    var url = '/project/' + response.refId + '/discussions/' + response.id;
                    return openState(st, url);
                }
            });

            return true;
        }

        if (section == 'wiki' || section == 'wikipage') {
            this.http.get(`/api/wiki/${refId}`)
            .toPromise()
            .then((response) => {
                if (response.section == 'project') {
                    var state = ['home.project.summary.wiki', {
                        id: response.refId,
                        pagename: response.pagename
                    }];
                    var url = '/project/' + response.refId + '/wiki?pagename=' + response.pagename;
                    return openState(state, url);
                }
                return false;
            });

            return true;
        }

        if (section == 'project-team-joins') {
            if (project) {
                return openState(
                    this.getStateForSectionRefId(
                        section, refId, project
                    ), ''
                );
            } else {
                this.http.get(`/api/project/${refId}`)
                .toPromise()
                .then((proj) => {
                    console.log('project-team-joins', proj);
                    return openState(
                        this.getStateForSectionRefId(
                            section, refId, proj?.unixName
                        ), ''
                    );
                });
            }
        }

        var state = this.getStateForSectionRefId(section, refId, project);
        if (!state) {
            return false;
        }

        var url = this.getUrlForSectionRefId(section, refId, project);
        return openState(state, url);
    }

    goToActivity(activity: any, options: any) {
        this.goToItem(activity.section, this.getActivityRefId(activity), options, _.get(activity, 'project.unixName', ''));
    }

    /**
     * URL of an activity's item, for a real href, or false when the section
     * has no static URL (wiki and forum items need a lookup; use goToActivity).
     */
    getActivityUrl(activity: any): string | false {
        if (!activity) {
            return false;
        }
        const refId = this.getActivityRefId(activity);
        if (!refId) {
            return false;
        }
        return this.getUrlForSectionRefId(activity.section, refId, _.get(activity, 'project.unixName', ''));
    }

    /**
     * The id goToItem expects for an activity. Build activities are logged
     * against the job, so the build's own id comes from extendedData.
     */
    private getActivityRefId(activity: any) {
        if (activity.section == 'commit' ||
            activity.section == 'scmcommit') {
            return _.get(activity, 'extendedData.new_version', _.get(activity, 'refId', ''));
        } else if (activity.section == 'build') {
            return _.get(activity, 'extendedData.id', 0);
        }
        return activity.refId;
    }
}